import * as React from 'react';
import { useTranslate } from '@tolgee/react';
import { Button, Drawer, Dropdown, Menu, Space } from 'antd';
import { useFormikContext } from 'formik';
import { css, cx } from '@emotion/css';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  CloseEditSkuButtonProvider,
  CurrentKnowledgeValueContext,
  SyncCheckboxSelectProvider,
} from '@knowledge/knowledges/singleClass/context';
import { contentRender } from '@knowledge/knowledges/spu/Knowledge';
import { useGetSegmentsValue } from '@knowledge/common/share';
import { findIndex, get, find } from 'lodash-es';
import { SpuAreaInner } from '@knowledge/knowledges/singleClass/SpuArea';
import { SpuClassDirtyContextProvider } from '@knowledge/knowledges/singleClass/context';
import { DownOutlined, CheckOutlined } from '@ant-design/icons';
import { countSku } from '@knowledge/common/helpers/sku';
import { translateSegment } from '@knowledge/knowledges/singleClass/ValuesArea';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useSize } from 'ahooks';

// 扩大sku编辑区域， 抽屉
export const EditSkuButton = React.memo((props: { tabKey: string }) => {
  const { tabKey } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = React.useState(false);
  const { values } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  const { active, setActive } = useContext(CurrentKnowledgeValueContext);
  const skuCount = useMemo(() => {
    const index = findIndex(values.values, { id: active });
    return countSku(get(values, `values.${index}.spus`, []), 'skus');
  }, [values, active]);
  const { getSegmentsValue } = useGetSegmentsValue(values.valueAsDirectAnswer);
  const [key, setKey] = useState(tabKey);
  const [translatedSegment, setTranslatedSegment] = useState<Knowledge.SPU.Segment[]>(undefined!);
  const currentKc = find(values.values, { id: active });
  const systemLang = useSystemLanguageValue();
  const ref = useRef<HTMLDivElement>(null!);
  const size = useSize(ref);

  useEffect(() => {
    setTranslatedSegment(undefined!);
  }, [visible]);
  if (currentKc === undefined) {
    return null;
  }

  return (
    <>
      <Button type="link" onClick={() => setVisible(true)}>
        {t('large_edit_area')}
      </Button>
      <SyncCheckboxSelectProvider>
        <CloseEditSkuButtonProvider onClose={() => setVisible(false)}>
          {visible && (
            <Drawer
              destroyOnClose
              visible={visible}
              onClose={() => setVisible(false)}
              title={values.knowledgeName}
              width={1146}
              className={css`
                .ant-drawer-body {
                  padding: 16px 16px 0px;
                  margin: 0;
                }
              `}
              footer={
                <>
                  <div
                    className={key === 'yet' ? 'h-0 overflow-hidden' : ''}
                    id="unConfigFooterPortal"
                  ></div>
                  <div
                    className={key === 'notyet' ? 'h-0 overflow-hidden' : ''}
                    id="configFooterPortal"
                  ></div>
                </>
              }
            >
              <div className="h-full flex flex-col">
                <div className="relative">
                  <div
                    className="border border-solid border-[var(--fett-border-default)] p-[16px] max-h-[90px] overflow-auto flex-shrink-0"
                    style={{
                      paddingRight: size?.width ? size.width + 16 : 16,
                    }}
                  >
                    {contentRender(translatedSegment || getSegmentsValue(currentKc))}
                  </div>
                  <div
                    className="absolute top-[50%] right-[20px] flex items-center translate-y-[-50%]"
                    ref={ref}
                  >
                    {values.valueAsDirectAnswer && (
                      <Button
                        onClick={() => {
                          translateSegment(getSegmentsValue(currentKc), undefined, systemLang).then(
                            (res) => {
                              setTranslatedSegment(res);
                            },
                          );
                        }}
                        type="link"
                      >
                        {t('phrase.翻译')}
                      </Button>
                    )}
                    <Dropdown
                      overlay={
                        <Menu className="w-[900px]">
                          {values.values.map((each) => {
                            return (
                              <Menu.Item
                                onClick={() => {
                                  setTranslatedSegment(undefined!);
                                  setActive(each.id);
                                }}
                                className={css`
                                  span {
                                    display: flex;
                                    justify-content: space-between;
                                    width: 100%;
                                    align-items: center;
                                  }
                                `}
                              >
                                <div>{contentRender(getSegmentsValue(each))}</div>
                                <div className="flex-shrink-0 ml-[20px] flex items-center gap-[8px]">
                                  <div className="h-[24px] px-[8px] flex items-center border border-solid border-[#D6E8FF] text-[var(--fett-scenario-accent-text)]">
                                    {t('config_yet', {
                                      x: countSku(get(each, 'spus', []), 'skus'),
                                    })}
                                  </div>
                                  <CheckOutlined
                                    className={cx('!text-[#3370FF]', {
                                      invisible: each.id !== active,
                                    })}
                                  />
                                </div>
                              </Menu.Item>
                            );
                          })}
                        </Menu>
                      }
                    >
                      <Space>
                        <div className="h-[24px] px-[8px] flex items-center border border-solid border-[#D6E8FF] text-[var(--fett-scenario-accent-text)]">
                          {t('config_yet', { x: skuCount })}
                        </div>
                        <DownOutlined />
                      </Space>
                    </Dropdown>
                  </div>
                </div>
                <div className="flex-grow overflow-hidden border border-solid border-[var(--fett-border-default)] mt-[12px] flex-grow flex flex-col bg-[var(--fett-bg-panel)] overflow-hidden h-full">
                  <SpuClassDirtyContextProvider>
                    <SpuAreaInner
                      onKeyChange={(key) => {
                        setKey(key);
                      }}
                      tabKey={tabKey}
                      drawer
                      knowledgeName={values.knowledgeName}
                    />
                  </SpuClassDirtyContextProvider>
                </div>
              </div>
            </Drawer>
          )}
        </CloseEditSkuButtonProvider>
      </SyncCheckboxSelectProvider>
    </>
  );
});

EditSkuButton.displayName = 'EditSkuButton';
